import { batch } from 'react-redux';
import delay from '@redux-saga/delay-p';
import { subscribe } from 'subscribe-ui-event';
import { put, race, select, spawn, take, takeEvery } from 'typed-redux-saga/macro';

import {
  I13N_ACTION_CHANGE_VALUE,
  I13N_CATEGORY_SETTINGS,
  I13N_TYPE_FEATURE_COUNT,
} from 'Lib/constants/i13nSpec';
import { ONE_MINUTE, ONE_SECOND } from 'Lib/constants/time';
import { debugLocation as debug } from 'Lib/debug';
import getI13nUtils from 'Lib/i13n/getI13nUtils';
import { Store } from 'Lib/reduxType';
import isExpired from 'Lib/utils/isExpired';

import { getBrowserFamily } from 'Store/context/selectors';
import { fetchMeta } from 'Store/meta/actions';

import { trackEvent } from 'Store/sparks/actions';
import { ACCOUNT_PERMISSION_GRANT, ACCOUNT_PERMISSION_PROMPT } from 'Store/sparks/sparks.events';

import {
  locationPermissionDenied,
  locationPermissionGranted,
  locationTimeout,
  locationTimestampUpdate,
  locationUpdate,
  locationUpdateFailure,
} from './actions';
import { LOCATION_PERMISSION_DISMISS, LOCATION_UPDATE } from './constants';
import { getPosition } from './selectors';
import {
  DISTANCE_FILTER,
  getDistance,
  LOCATION_PERMISSION_IN_USE,
  LOCATION_PERMISSION_NEVER,
} from './utils';

const GA_GEO_LOCATION_DENIED = 'Denied';
const GA_GEO_LOCATION_ERROR = 'Error';
const GA_GEO_LOCATION_NOT_SUPPORTED = 'Not Supported';
const GA_GEO_LOCATION_TIMEOUT = 'Timeout';

const GEO_LOCATION_TIMEOUT = ONE_SECOND * 25;

const LOCATION_WATCH_TTL = ONE_MINUTE; // Cache for 1 min
const LOCATION_MAX_AGE = ONE_MINUTE * 30; // cache for 30 mins initial time
const LOCATION_STALE = ONE_MINUTE * 5; // stale-while-revalidate

// https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions
const geoOptions = {
  enableHighAccuracy: true,
  maximumAge: LOCATION_WATCH_TTL,
  timeout: GEO_LOCATION_TIMEOUT,
};

const { executeEvent } = getI13nUtils();

export function* locationInitSaga() {
  // if location is already is state, proceed
  // otherwise, wait for LOCATION_UPDATE
  const position = yield* select(getPosition);

  const lat = position?.lat;
  const lon = position?.lon;
  const timestamp = position?.timestamp || 0;
  const isLocationExpired = isExpired(timestamp, LOCATION_MAX_AGE);
  debug('locationInitSaga', { lat, lon, isExpired });

  if (!lat || !lon || isLocationExpired) {
    const { timeout, dismiss } = yield* race({
      update: take(LOCATION_UPDATE),
      timeout: delay(GEO_LOCATION_TIMEOUT),
      dismiss: take(LOCATION_PERMISSION_DISMISS),
    });

    if (timeout || dismiss) {
      yield* put(locationTimeout());
    }
  }
  return yield* spawn(watchLocationSaga);
}

function* watchLocationSaga() {
  yield* takeEvery(LOCATION_UPDATE, locationPingSaga);
}

export function* locationPingSaga() {
  debug('location change, update meta');
  yield* put(fetchMeta(true));
}

function sendLocationBeacon(label) {
  const baseConfig = {
    category: I13N_CATEGORY_SETTINGS,
    action: I13N_ACTION_CHANGE_VALUE,
  };

  executeEvent(I13N_TYPE_FEATURE_COUNT, {
    ...baseConfig,
    label: 'LocationLogic',
    message: `Permission - ${label}`,
    sampleRate: 0.01,
  });
}
class LocationTracker {
  geoWatcherID: ReturnType<typeof window.navigator.geolocation.watchPosition> | null;
  hasFirstResponse: boolean;
  isEnabled: boolean;
  isSafari: boolean;
  isSupported: boolean;
  store: Store | null;
  subscription: ReturnType<typeof subscribe> | null;
  unloadListenerAdded: boolean;

  constructor() {
    this.geoWatcherID = null;
    this.hasFirstResponse = false;
    this.isEnabled = false;
    this.isSupported = false;
    this.store = null;
    this.subscription = null;
    this.unloadListenerAdded = false; // needed for browsers that don't fire visibilitychange on tab close
    this.isSafari = false;
  }

  init(store: Store) {
    // @TODO, should we pass the whole store here?
    this.store = store;
    this.isSupported = __CLIENT__ ? 'geolocation' in navigator : false;
    this.isSafari = getBrowserFamily(store.getState()).includes('Safari');
  }

  // @TODO, integrates with page lifecycle API
  visibilityChangeHandler = () => {
    const { visibilityState } = document;
    // could be background/minimize/tab switch/close
    if (visibilityState === 'hidden') {
      this.cleanup();
    }
    if (visibilityState === 'visible') {
      this.watch();
    }
  };

  handleTabChanges = () => {
    if (!this.subscription) {
      this.subscription = subscribe('visibilitychange', this.visibilityChangeHandler);
    }

    if (this.isSafari && !this.unloadListenerAdded) {
      // Safari does not reliably fire the `pagehide` or `visibilitychange`
      // events when closing a tab
      // - https://bugs.webkit.org/show_bug.cgi?id=151610
      // - https://bugs.webkit.org/show_bug.cgi?id=151234
      this.unloadListenerAdded = true;
      window.addEventListener('beforeunload', this.cleanup, true);
    }
  };

  watch = () => {
    debug('start watch task');
    if (!this.isSupported) {
      sendLocationBeacon(GA_GEO_LOCATION_NOT_SUPPORTED);
      return;
    }

    if (this.geoWatcherID) {
      debug('watch task already exists');
      return;
    }

    this.handleTabChanges();

    // This is the best we can do in the browser... it won't prompt
    // if the user has already allowed this site to access location,
    // but there's no way for us to know that ahead of time.
    this.store?.dispatch(trackEvent(ACCOUNT_PERMISSION_PROMPT));

    const onPositionUpdate: PositionCallback = (position) => {
      if (!this.hasFirstResponse) {
        this.hasFirstResponse = true;
        this.store?.dispatch(
          trackEvent(ACCOUNT_PERMISSION_GRANT, { locationPermission: LOCATION_PERMISSION_IN_USE })
        );
      }

      const newPos = {
        lat: position.coords.latitude,
        lon: position.coords.longitude,
        timestamp: Date.now(),
      };
      const globalState = this.store?.getState();
      if (!globalState || !this.store) {
        return;
      }
      const lastPos = getPosition(globalState);
      debug('onPositionUpdate', { newPos, lastPos });

      if (!lastPos) {
        debug('No previous location, update new location');
        this.store.dispatch(locationUpdate(newPos));
        return;
      }
      const distance = getDistance(lastPos, newPos);

      // max-age to keep the location
      const isLastPosExpired = isExpired(lastPos.timestamp, LOCATION_MAX_AGE);
      // is last position stale
      const isLastPosStale = isExpired(lastPos.timestamp, LOCATION_STALE);
      const isMeaningfulDistanceChange = distance > DISTANCE_FILTER;
      debug('onPositionUpdate', { isLastPosExpired, isLastPosStale, isMeaningfulDistanceChange });

      // update location if there is meaningful change or persist time expired
      if (isMeaningfulDistanceChange || isLastPosExpired) {
        debug('Update new location', { isMeaningfulDistanceChange, isLastPosExpired });
        this.store.dispatch(locationUpdate(newPos));
        return;
      }

      // if there is no meaningful changes but the stale time expire,
      // we update the timestamp to keep our cache fresh without updating location
      if (isLastPosStale) {
        debug('Location stale, update cache');
        this.store.dispatch(locationTimestampUpdate(newPos.timestamp));
        return;
      }
      debug('Location still fresh, skip update');
    };

    const onPositionUpdateFail: PositionErrorCallback = (err) => {
      if (!this.store) {
        return;
      }
      debug('position error', err);
      const { PERMISSION_DENIED = 1, TIMEOUT = 3 } =
        window.GeolocationPositionError || window.PositionError || {}; // non-standard PositionError is for old browser
      const { code } = err || {};
      // User denied
      if (code === PERMISSION_DENIED) {
        this.store.dispatch(locationPermissionDenied(err));

        if (!this.hasFirstResponse) {
          this.hasFirstResponse = true;
          this.store.dispatch(
            trackEvent(ACCOUNT_PERMISSION_GRANT, { locationPermission: LOCATION_PERMISSION_NEVER })
          );
        }
        sendLocationBeacon(GA_GEO_LOCATION_DENIED);
      } else {
        const isTimeout = code === TIMEOUT;
        batch(() => {
          this.store?.dispatch(locationPermissionGranted());
          this.store?.dispatch(locationUpdateFailure(err));
        });
        sendLocationBeacon(isTimeout ? GA_GEO_LOCATION_TIMEOUT : GA_GEO_LOCATION_ERROR);
      }
    };

    this.geoWatcherID = window.navigator.geolocation.watchPosition(
      onPositionUpdate,
      onPositionUpdateFail,
      geoOptions
    );
    debug('geo watch task started', this.geoWatcherID);
  };

  cleanup = () => {
    if (this.geoWatcherID) {
      debug('cleaning up watch task', this.geoWatcherID);
      window.navigator.geolocation.clearWatch(this.geoWatcherID);
      this.geoWatcherID = null;
    }
  };
}

export default new LocationTracker();
